import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Container } from "@material-ui/core";
import { useQueryClient } from "react-query";
import { CAutocomplete, ProgressButton, Text, VFlex } from "shared";
import { useId } from "utils/hooks";
import { delay, queryKeys } from "utils/misc";
import { FORM_NOTIFICATION_DELAY } from "theme";
import usePatient from "routes/patient/queries/usePatient";
import useChangePhysician from "routes/patient/queries/useChangePhysician";

type Physician = {
  id: number;
  first_name: string;
  last_name: string;
};

interface Props {
  physicians: Physician[];
  physiciansIsLoading: boolean;
  onClose: () => void;
}

const ChangePhysicianForm: React.VFC<Props> = ({ physicians, physiciansIsLoading, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const id = useId();
  const { data: patient } = usePatient();
  const { mutateAsync, isLoading } = useChangePhysician();
  const form = useForm();

  if (!patient) return null;

  const onSubmit = form.handleSubmit(async fields => {
    const patientKey = queryKeys.physicians(id);

    try {
      await mutateAsync({ physician_id: fields.physician_id });
      await delay(FORM_NOTIFICATION_DELAY);
      onClose();
    } finally {
      queryClient.invalidateQueries(patientKey);
    }
  });

  return (
    <Container>
      <Text heading bold id="change-physician">
        {t("form.change_physician")}
      </Text>

      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <CAutocomplete
            getOptionValue={({ id }) => id}
            getOptionLabel={({ first_name, last_name }) => `${first_name} ${last_name}`}
            loading={physiciansIsLoading}
            options={physicians ?? []}
            name="physician_id"
            label={t("form.physician")}
            margin="normal"
            size="small"
            fullWidth
          />
          <VFlex alignItems="center" gap={2} my={5}>
            <ProgressButton loading={isLoading}>{t("buttons.save")}</ProgressButton>
            <Button disabled={isLoading} onClick={onClose}>
              {t("buttons.cancel")}
            </Button>
          </VFlex>
        </form>
      </FormProvider>
    </Container>
  );
};

export default ChangePhysicianForm;
